import { Button } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import { AddCircleLine, AddLine } from "@dropbox/dig-icons/assets";
import { PAP_Initiate_AddStackSection } from "@mirage/analytics/events/types/initiate_add_stack_section";
import { createUxaElementId } from "@mirage/analytics/uxa";
import { IconButtonWithTooltip } from "@mirage/shared/icons/IconButtonWithTooltip";
import { useReportStackPapEvent } from "@mirage/stacks/hooks/useReportStackPapEvent";
import i18n from "@mirage/translations";
import { useCallback } from "react";
import { SectionsActionType } from "../../types/FullScreenTypes";
import styles from "./Navigation.module.css";

import type { SetStateAction } from "react";

export const CreateSectionButton = ({
  disabled,
  setIsEditing,
  setCurrentAction,
  isAugustRevisionEnabled,
}: {
  disabled: boolean;
  setIsEditing: (isEditing: SetStateAction<boolean>) => void;
  setCurrentAction: (action: SectionsActionType | null) => void;
  // this override flag is used to display the dfb august revision styles
  // when we want to render the new styles but can't feature gate users
  // ex. public view of the stacks page using (dfi styles, and we wanted to display dfb styles)
  isAugustRevisionEnabled?: boolean;
}) => {
  const reportStackPapEvent = useReportStackPapEvent();

  const onClick = useCallback(() => {
    setCurrentAction(SectionsActionType.CREATE_SECTION);
    setIsEditing(true);
    reportStackPapEvent(PAP_Initiate_AddStackSection());
  }, [setIsEditing, setCurrentAction, reportStackPapEvent]);

  if (isAugustRevisionEnabled) {
    return (
      <IconButtonWithTooltip
        variant="transparent"
        tooltipProps={{ title: i18n.t("create_section") }}
        aria-label={i18n.t("create_section")}
        onClick={onClick}
        data-uxa-log={createUxaElementId("add_section_button", {
          featureLine: "stacks",
        })}
        data-testid="Stacks-createSectionButton"
        disabled={disabled}
      >
        <UIIcon src={AddCircleLine} />
      </IconButtonWithTooltip>
    );
  } else {
    return (
      <Button
        variant="borderless"
        aria-label={i18n.t("create_section")}
        withIconStart={<UIIcon src={AddLine} />}
        onClick={onClick}
        data-uxa-log={createUxaElementId("add_section_button", {
          featureLine: "stacks",
        })}
        data-testid="Stacks-createSectionButton"
        disabled={disabled}
        className={styles.addSectionButtonV2}
        size="large"
        fullWidth
      >
        <Text color={disabled ? "faint" : "standard"}>
          {i18n.t("create_section")}
        </Text>
      </Button>
    );
  }
};
